// tab/src/components/Layout/Layout.tsx
// OnRounds Phase 2 - Main Layout Component

import React, { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
  Badge,
  Avatar,
  Text,
  Tooltip,
  Divider,
} from '@fluentui/react-components';
import {
  Clock24Regular,
  People24Regular,
  Mail24Regular,
  Eye24Regular,
  DocumentSearch24Regular,
  Settings24Regular,
  PersonCircle24Regular,
} from '@fluentui/react-icons';
import { useUser } from '../../context/UserContext';
import { useTeam } from '../../context/TeamContext';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '20px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('16px'),
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  logoText: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorBrandForeground1,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('16px'),
  },
  shiftStatus: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('6px', '12px'),
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  shiftActive: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  nav: {
    ...shorthands.padding('0', '20px'),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  main: {
    flexGrow: 1,
    ...shorthands.overflow('auto'),
    ...shorthands.padding('20px'),
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
});

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentShift } = useUser();
  const { currentTeam } = useTeam();

  const tabs = [
    { value: '/', label: 'Clock On/Off', icon: <Clock24Regular /> },
    { value: '/directory', label: 'Directory', icon: <People24Regular /> },
    { value: '/messages', label: 'Messages', icon: <Mail24Regular /> },
  ];

  // Add supervisor/admin tabs based on permissions
  if (user?.isSupervisor || user?.isAdmin) {
    tabs.push({ value: '/supervisor', label: 'Supervisor', icon: <Eye24Regular /> });
    tabs.push({ value: '/audit', label: 'Audit', icon: <DocumentSearch24Regular /> });
  }
  
  if (user?.isAdmin) {
    tabs.push({ value: '/admin', label: 'Admin', icon: <Settings24Regular /> });
  }

  const handleTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    navigate(data.value as string);
  };

  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.startsWith('/messages')) return '/messages';
    if (path.startsWith('/admin')) return '/admin';
    return path;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.logo}>
            <Clock24Regular />
            <span className={styles.logoText}>OnRounds</span>
          </div>
          {currentTeam && (
            <>
              <Divider vertical style={{ height: '24px' }} />
              <Text weight="semibold">{currentTeam.name}</Text>
            </>
          )}
        </div>

        <div className={styles.headerRight}>
          {/* Shift Status */}
          <div className={`${styles.shiftStatus} ${currentShift ? styles.shiftActive : ''}`}>
            {currentShift ? (
              <>
                <Badge color="success" size="small" />
                <Text size={200}>
                  On as <strong>{currentShift.roleName}</strong>
                </Text>
              </>
            ) : (
              <>
                <Badge color="subtle" size="small" />
                <Text size={200}>Not clocked on</Text>
              </>
            )}
          </div>

          {/* User Info */}
          {user && (
            <Tooltip content={user.email} relationship="label">
              <div className={styles.userInfo}>
                <Avatar
                  name={user.displayName}
                  size={32}
                  badge={user.isAdmin ? { status: 'available' } : undefined}
                />
                <Text size={200}>{user.displayName}</Text>
              </div>
            </Tooltip>
          )}
        </div>
      </header>

      {/* Navigation */}
      <nav className={styles.nav}>
        <TabList
          selectedValue={getCurrentTab()}
          onTabSelect={handleTabSelect}
          size="medium"
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} icon={tab.icon}>
              {tab.label}
            </Tab>
          ))}
        </TabList>
      </nav>

      {/* Main Content */}
      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
};
